import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import AddItemButton from 'analysis-tool/data-frame-creation/steps/AddItemButton';
import {getModelTypeConstantForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import TextInput from 'ui-library/inputs/TextInput';
import {immutableRemoveAt} from 'util/ImmutableListUtil';
import {capitalize, typeToName} from 'util/NameUtil';
import {JSONValue, Override} from 'util/Types';

import {CustomComponentEditorProps, TaggedUnionUISchema} from '../../StepConfigEditorTypes';
import StepConfigEditorRouter from '../StepConfigEditorRouter';
import {VariableHeaderOrNull} from '../StepConfigFieldsTemplate';
import TaggedUnionEditor from '../TaggedUnionEditor';

const VARIABLE_DEFINITION_CONFIG_SHAPE =
  PIPELINE_STEP_SHAPES[PipelineStepType.DEFINE_TEMPLATE_VARIABLES][0].config.variables.elementSpec.fields
    .definition;

// Convert allowed domain types (e.g. 'PLATE') to user-facing names
const DOMAIN_TYPE_DISPLAY_NAMES = Object.fromEntries(
  VARIABLE_DEFINITION_CONFIG_SHAPE.choices[5].fields.types.elementSpec.choices.map((type) => [
    type,
    capitalize(typeToName(getModelTypeConstantForObjectType(type))),
  ])
);

const VARIABLE_DEFINITION_UI_SCHEMA: TaggedUnionUISchema = {
  tagKeyDisplayNames: {
    boolean: 'Boolean',
    integer: 'Integer',
    number: 'Decimal',
    string: 'Text',
    domain: 'Benchling type',
  },
  placeholder: 'Select variable type',
  choiceSettings: {
    kind: {displayName: 'Type'},
    types: {
      displayName: 'Benchling type',
      fieldSettings: {
        defaultValue: 'PLATE',
        displayNames: DOMAIN_TYPE_DISPLAY_NAMES,
      },
    },
    required: {displayName: 'Required'},
    minValue: {displayName: 'Minimum value'},
    maxValue: {displayName: 'Maximum value'},
  },
};

const DEFAULT_VARIABLE: VariableConfig = {
  name: '',
  definition: {kind: 'boolean', required: true},
  value: null,
};

type VariableConfig = PipelineStepConfig<
  PipelineStepType.DEFINE_TEMPLATE_VARIABLES,
  0,
  'draft'
>['variables'][number];

type VariableDefinition = VariableConfig['definition'];

function convertDefinitionToArray(
  definition: VariableDefinition
): Extract<VariableDefinition, {kind: 'array'}> {
  return definition?.kind === 'array'
    ? definition
    : {kind: 'array', required: true, elementSpec: definition ?? {kind: 'boolean', required: true}};
}

function convertDefinitionFromArray(
  definition: VariableDefinition
): Exclude<VariableDefinition, {kind: 'array'}> {
  return definition?.kind === 'array' ? definition?.elementSpec : definition;
}

interface VariableDefinitionEditorProps {
  config: Exclude<VariableConfig['definition'], {kind: 'array'}>;
  onChangeConfig: (config: Exclude<VariableConfig['definition'], {kind: 'array'}>) => void;
  disabled?: boolean;
}

/**
 * Editor to configure the variable's definition.
 */
function VariableDefinitionEditor({
  config,
  onChangeConfig,
  disabled,
}: VariableDefinitionEditorProps): JSX.Element {
  return (
    <TaggedUnionEditor
      config={config}
      // Use nested `elementSpec` config shape, so that users cannot select 'array' as a type; instead,
      // 'array' types are controlled by the "Multi-select" checkbox.
      configShape={VARIABLE_DEFINITION_CONFIG_SHAPE.choices[0].fields.elementSpec}
      onChangeConfig={onChangeConfig}
      disabled={disabled}
      uiSchema={VARIABLE_DEFINITION_UI_SCHEMA}
    />
  );
}

interface VariableValueEditorProps {
  definition: VariableConfig['definition'];
  config: VariableConfig['value'];
  onChangeConfig: (config: VariableConfig['value']) => void;
  disabled?: boolean;
}

/**
 * Editor to configure the variable's value.
 */
function VariableValueEditor({
  definition,
  config,
  onChangeConfig,
  disabled,
}: VariableValueEditorProps): JSX.Element {
  if (definition?.kind == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="Select a variable type." />;
  }
  if (definition?.required == null) {
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="Select if the variable is required." />;
  }

  return (
    <>
      <StepConfigEditorRouter
        config={{value: config}}
        configShape={{kind: 'shape', fields: {value: definition as PipelineConfigJsonSpec}, required: true}}
        onChangeConfig={({value}: {value: JSONValue}) => {
          onChangeConfig(value);
        }}
        uiSchema={{value: {displayName: 'Value'}}}
        isTopLevel={true}
        disabled={disabled}
        dataTestAttrs={{}}
      />
      <div>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text="The value above is replaced each time the template is applied."
        />
      </div>
    </>
  );
}

interface VariableEditorProps {
  index: number;
  config: VariableConfig;
  onChangeConfig: (config: VariableConfig) => void;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

/**
 * Editor to configure a variable's name, definition and value.
 *
 *  `VariableEditor` uses multiple
 * @package
 */
export function VariableEditor({
  index,
  config,
  onChangeConfig,
  onRemove,
  canRemove,
  disabled,
}: VariableEditorProps): JSX.Element {
  return (
    <div className={css(styles.variableEditor)}>
      <Row>
        <Col sm={6}>
          <VariableHeaderOrNull variableName="Name" isCompact={false} />
          <TextInput
            value={config.name ?? ''}
            placeholder="Name"
            onChange={(e) => {
              onChangeConfig({...config, name: e.target.value});
            }}
            disabled={disabled}
          />
        </Col>
        <Col sm={5}>
          <VariableHeaderOrNull variableName="Multi-select" isCompact={false} />
          <CheckboxInput
            id={`variable-is-multi-select-${index}`}
            label="Allow multiple values"
            checked={config.definition?.kind === 'array'}
            onChange={(e) => {
              if (e.target.checked) {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionToArray(config.definition),
                  value: [config.value],
                });
              } else {
                onChangeConfig({
                  ...config,
                  definition: convertDefinitionFromArray(config.definition),
                  value: Array.isArray(config.value) && config.value.length > 0 ? config.value[0] : null,
                });
              }
            }}
          />
        </Col>
        <Col sm={1}>
          <div className={css(styles.removeIcon)}>
            <Tooltip text={canRemove ? 'Remove variable' : 'Cannot remove the last variable'}>
              <DeleteIcon icon="trash-can" disabled={!canRemove || disabled} onClick={onRemove} />
            </Tooltip>
          </div>
        </Col>
      </Row>
      <VariableDefinitionEditor
        config={convertDefinitionFromArray(config.definition)}
        onChangeConfig={(definition) => {
          onChangeConfig({
            ...config,
            ...(config.definition?.kind === 'array'
              ? {definition: convertDefinitionToArray(definition), value: []}
              : {definition, value: null}),
          });
        }}
        disabled={disabled}
      />
      <VariableValueEditor
        definition={config.definition}
        config={config.value}
        onChangeConfig={(value) => {
          onChangeConfig({...config, value});
        }}
        disabled={disabled}
      />
    </div>
  );
}

type DefineTemplateVariablesEditorProps = Override<
  CustomComponentEditorProps,
  {config: PipelineStepConfig<PipelineStepType.DEFINE_TEMPLATE_VARIABLES, 0, 'draft'>}
>;

/**
 * Custom editor for `DEFINE_TEMPLATE_VARIABLES` steps.
 *
 * In `DEFINE_TEMPLATE_VARIABLES` steps, each variable has a definition (`config.variables[0].definition`),
 * and a value which conforms to that definition (`config.variables[0].value`).
 *
 * Definitions' format is a narrowed version of `PipelineConfigJsonSpec`.
 *
 * A custom editor is used, so variables' values can be updated dynamically with their definition.
 */
export default function DefineTemplateVariablesEditor({
  config,
  onChangeConfig,
  disabled,
}: DefineTemplateVariablesEditorProps): JSX.Element {
  const variables = config.variables ?? [];

  return (
    <>
      {variables.map((variable, index) => (
        <VariableEditor
          key={`variable-editor-${index}`}
          index={index}
          config={variable}
          onChangeConfig={(variable) => {
            onChangeConfig(config, ['variables'], config.variables.toSpliced(index, 1, variable));
          }}
          onRemove={() => {
            onChangeConfig(config, ['variables'], immutableRemoveAt(variables, index));
          }}
          canRemove={variables.length > 1}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <div className="u-moderate-spacing-top">
          <AddItemButton
            onClick={() => {
              onChangeConfig(config, ['variables'], [...variables, {...DEFAULT_VARIABLE}]);
            }}
            label="Add variable"
          />
        </div>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  removeIcon: {
    float: 'right',
  },
  variableEditor: {
    background: GRAY_LIGHTER,
    padding: 10,
    marginBottom: 10,
  },
});
